/**
 * applyBodyBackground utility module.
 * Provides functions to apply and remove the Wecom gradient background from document.body
 * with reference counting to handle multiple shortcodes on the same page.
 */

// Store the original body background before any modifications
let originalBodyBackground = null;

// Reference counter to track how many Wecom apps are mounted
let mountedAppsCount = 0;

// Fallback background value (matching --color-background-primary from variables.css)
// Used only if the CSS variable is not available
const FALLBACK_BACKGROUND =
  "linear-gradient(92.18deg, rgba(255, 159, 140, 0.22), rgba(255, 184, 174, 0.18) 29.48%, rgba(255, 209, 207, 0.22) 50.27%, rgba(251, 232, 229, 0.22) 72.87%, rgba(255, 255, 255, 0.22) 96.15%), #fff";

// Style element for injecting CSS with !important
let wecomStyleElement = null;

/**
 * Gets the Wecom background color from the CSS variable --color-background-primary.
 * Falls back to the hardcoded value if the variable is not available.
 *
 * @returns {string} The background value to use
 */
function getWecomBackground() {
  if (typeof document === "undefined") {
    return FALLBACK_BACKGROUND;
  }

  // Try to read the CSS variable from :root
  const rootStyles = getComputedStyle(document.documentElement);
  const backgroundValue = rootStyles
    .getPropertyValue("--color-background-primary")
    .trim();

  // Return the CSS variable value if available, otherwise fall back to hardcoded value
  return backgroundValue || FALLBACK_BACKGROUND;
}

/**
 * Applies the Wecom gradient background to document.body.
 * Uses reference counting to ensure the background persists as long as any Wecom app is mounted.
 * Reads the background value from the CSS variable --color-background-primary for consistency.
 *
 * @returns {void}
 */
export function applyWecomBodyBackground() {
  // Store the original background only on the first mount
  if (mountedAppsCount === 0) {
    originalBodyBackground = document.body.style.background || "";

    // Get the background value from CSS variable (single source of truth)
    const backgroundValue = getWecomBackground();

    // Create and inject CSS rule with !important
    wecomStyleElement = document.createElement("style");
    wecomStyleElement.setAttribute("data-wecom-body-background", "true");
    wecomStyleElement.textContent = `body { background: ${backgroundValue} !important; }`;
    document.head.appendChild(wecomStyleElement);
  }

  // Increment the reference counter
  mountedAppsCount++;
}

/**
 * Removes the Wecom gradient background from document.body.
 * Uses reference counting to only restore the original background when the last app unmounts.
 *
 * @returns {void}
 */
export function removeWecomBodyBackground() {
  // Decrement the reference counter
  mountedAppsCount = Math.max(0, mountedAppsCount - 1);

  // Only restore the original background when no apps are mounted
  if (mountedAppsCount === 0) {
    // Remove the injected CSS rule
    if (wecomStyleElement && wecomStyleElement.parentNode) {
      wecomStyleElement.parentNode.removeChild(wecomStyleElement);
      wecomStyleElement = null;
    }

    // Restore original background
    document.body.style.background = originalBodyBackground || "";
    originalBodyBackground = null;
  }
}

/**
 * Gets the current reference count (for debugging purposes).
 *
 * @returns {number} The number of currently mounted Wecom apps
 */
export function getMountedAppsCount() {
  return mountedAppsCount;
}
